import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, Box, Globe, Scale, AlignLeft, Tag, MessageSquare, Ban, FilePlus, RefreshCw, CheckCircle, ImagePlus, FileDown, Check } from 'lucide-react';
import { getTestOrder, createIncomingInvoice } from '../services/OrderApi';
import { getSystemUserRefKey } from '../services/systemAuth';
import {
  buildCertificateRows,
  buildCertificateRowsForInvoice,
  fillCertificateTemplate,
  saveBlobWithPicker,
} from '../services/certificateExcel';
import { getDriveLinkFromComment, getProgressesFolderLink, buildDrivePath, DRIVE_LINE_PREFIX, DRIVE_PATH_PREFIX } from '../services/googleDrive';
import { TestOrder, PurchaseInvoice } from '../types';

// Форматтер валюты с 2 знаками после запятой (без округления до целых)
const fmtMoney2Decimals = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Форматтер количества (2 знака после запятой)
const fmtQty = (n: number) => Number(n).toFixed(2);

/** Допуск при сравнении сумм (IDR, 2 знака) — устраняет ложные срабатывания из-за погрешности float и округления */
const AMOUNT_EPSILON = 1.0; // Увеличено до 1.0 IDR для учета погрешностей вычислений и различий в округлении между 1С и фронтендом

/**
 * Парсит комментарий накладной и извлекает данные о down payment, retention, PPH и Inc VAT.
 * Формат: "Down payment Y% - Rp Z", "Retention W% - Rp V", "PPH X% - Rp T", "Inc VAT: 11% - Rp V"
 */
/** Парсит сумму из строки комментария, поддерживая оба формата:
 *  id-ID (Chrome): 5.800.000,00  — точка-разделитель тысяч, запятая-десятичный
 *  en-US (Safari): 5,800,000.00  — запятая-разделитель тысяч, точка-десятичный */
function parseAmount(s: string): number {
  const cleaned = s.replace(/\s/g, '');
  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  if (lastComma > lastDot) {
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
  }
  if (lastDot > lastComma) {
    return parseFloat(cleaned.replace(/,/g, '')) || 0;
  }
  return parseFloat(cleaned) || 0;
}

/** Safari может форматировать IDR как "IDR" вместо "Rp"; пробел может быть \u00A0 / \u202F */
const CURRENCY_RE = '(?:Rp|IDR)[\\s\\u00A0\\u202F]*';

function parseInvoiceComment(comment: string | undefined): { downPaymentPct?: number; downPaymentAmount?: number; retentionPct?: number; retentionAmount?: number; pphPct?: number; pphAmount?: number; incVatAmount?: number } {
  if (!comment) return {};
  const lines = comment.split('\n');
  const result: { downPaymentPct?: number; downPaymentAmount?: number; retentionPct?: number; retentionAmount?: number; pphPct?: number; pphAmount?: number; incVatAmount?: number } = {};

  for (const line of lines) {
    const downMatch = line.match(new RegExp(`- Down payment\\s+(\\d+(?:\\.\\d+)?)%\\s+-\\s+${CURRENCY_RE}([\\d.,\\s]+)`, 'i'));
    if (downMatch) {
      result.downPaymentPct = parseFloat(downMatch[1]);
      result.downPaymentAmount = parseAmount(downMatch[2]);
    }
    const retentionMatch = line.match(new RegExp(`- Retention\\s+(\\d+(?:\\.\\d+)?)%\\s+-\\s+${CURRENCY_RE}([\\d.,\\s]+)`, 'i'));
    if (retentionMatch) {
      result.retentionPct = parseFloat(retentionMatch[1]);
      result.retentionAmount = parseAmount(retentionMatch[2]);
    }
    const pphMatch = line.match(new RegExp(`Exc PPH\\s+(\\d+(?:\\.\\d+)?)%\\s+-\\s+${CURRENCY_RE}([\\d.,\\s]+)`, 'i'));
    if (pphMatch) {
      result.pphPct = parseFloat(pphMatch[1]);
      result.pphAmount = parseAmount(pphMatch[2]);
    }
    const incVatMatch = line.match(new RegExp(`Inc VAT:\\s*11%\\s*-\\s*${CURRENCY_RE}([\\d.,\\s]+)`, 'i'));
    if (incVatMatch) {
      result.incVatAmount = parseAmount(incVatMatch[1]);
    }
  }
  return result;
}

interface OrderDetailsProps {
    orderRef: string; // ID заказа для отображения
}

// --- КОНФИГУРАЦИЯ ШИРИНЫ КОЛОНОК ---
// Используем фиксированную ширину, чтобы таблица не "прыгала"
const COL_WIDTHS = {
  INDEX: "w-[50px] min-w-[50px]",
  RUS: "w-[220px] min-w-[220px]",
  ENG: "w-[220px] min-w-[220px]",
  CONTENT: "w-[160px] min-w-[160px]",
  QTY: "w-[80px] min-w-[80px]",
  UNIT: "w-[60px] min-w-[60px]",
  PRICE: "w-[100px] min-w-[100px]",
  AMOUNT: "w-[120px] min-w-[120px]",
  // Правая часть (Накладные)
  INV_QTY: "w-[70px] min-w-[70px]",
  INV_AMT: "w-[130px] min-w-[130px]",
  INV_REMAINING: "w-[90px] min-w-[90px]",
  EDIT_ACCEPT_QTY: "w-[90px] min-w-[90px]",
  AMOUNT_ACCEPT: "w-[200px] min-w-[200px]",
  NO_PROGRESS: "w-[200px] min-w-[200px]"
};

export const OrderDetails = ({ orderRef }: OrderDetailsProps) => {
  const [data, setData] = useState<TestOrder[]>([]); // Данные заказа
  const [loading, setLoading] = useState(true); // Состояние загрузки
  const [error, setError] = useState(''); // Ошибка
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [createInvoiceMessage, setCreateInvoiceMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  /** Заказ, для которого открыт диалог подтверждения количеств Acceptance; null — диалог закрыт */
  const [confirmAcceptOrder, setConfirmAcceptOrder] = useState<TestOrder | null>(null);
  /** Редактируемое количество для приёмки по строкам: ключ = orderRef_idx, значение = строка для инпута */
  /** Down payment: тумблер и процент 0–100. Уменьшает сумму оплаты, но не стоимость строк накладной. */
  const [downPaymentOn, setDownPaymentOn] = useState(false);
  const [downPaymentPct, setDownPaymentPct] = useState<number>(0);
  /** Retention: тумблер и процент 0–100. Уменьшает сумму оплаты, но не стоимость строк накладной. */
  const [retentionOn, setRetentionOn] = useState(false);
  const [retentionPct, setRetentionPct] = useState<number>(0);
  /** Дата приёмки (для блока Acceptance), по умолчанию — текущая дата */
  const [acceptanceDate, setAcceptanceDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  /** Редактируемое количество для приёмки по строкам: ключ = orderRef_idx */
  const [editableAcceptQty, setEditableAcceptQty] = useState<Record<string, string>>({});
  /** Режим "Enter in %": показывать поле ввода процента и кнопку подтверждения */
  const [showEnterPct, setShowEnterPct] = useState(false);
  /** Значение процента при нажатии "Enter in %" (заполнение: количество из заказа × процент) */
  const [enterPctValue, setEnterPctValue] = useState(100);

  const clampPct = (n: number) => Math.max(0, Math.min(100, n));

  const parseAcceptQty = (val: string | undefined): number =>
    parseFloat(String(val || '0').replace(',', '.')) || 0;

  /**
   * Возвращает суммы Amount to accept по строкам.
   * (сумма по заказу / кол-во по заказу) × кол-во к приёмке. Вычеты применяются только к оплате.
   */
  const getCappedAcceptAmounts = (order: TestOrder): number[] => {
    const items = order.Запасы ?? [];
    return items.map((item, idx) => {
      const orderQty = Number(item.Количество) || 0;
      const rowKey = `${order.Ref_Key}_${idx}`;
      const acceptQtyNum = parseAcceptQty(editableAcceptQty[rowKey]);
      const orderAmount = Number(item.Сумма) || 0;
      const baseLine = orderQty !== 0 ? (orderAmount / orderQty) * acceptQtyNum : 0;
      return Math.max(0, baseLine);
    });
  };

  /** Собирает строки накладной по полной стоимости принятого объёма; вычеты влияют только на оплату. НДС включён в сумму строки и передаётся отдельным реквизитом. */
  const buildЗапасыForOrder = (order: TestOrder) => {
    const VAT_RATE_KEY = "7fba2c2f-f77d-11ef-813c-ac1f6b401e69";
    const VAT_RATE_KEY_WITH_TAX = "c7a2da29-d981-11f0-ae1b-ac1f6b4129e5";
    const totalOrderAmount = (order.Запасы ?? []).reduce((acc, i) => acc + (Number(i.Сумма) || 0), 0);
    const totalVatFromOrder = (order.Запасы ?? []).reduce((acc, i) => acc + (Number(i.СуммаНДС) || 0), 0);
    const cappedAmounts = getCappedAcceptAmounts(order);
    const rows: { LineNumber: number; Номенклатура_Key: string; Характеристика_Key?: string; Количество: number; Сумма: number; Цена: number; ЕдиницаИзмерения: string; ЕдиницаИзмерения_Type: "StandardODATA.Catalog_КлассификаторЕдиницИзмерения"; СтавкаНДС_Key: string; СуммаНДС?: number; Содержание: string; Всего: number }[] = [];
    let lineNumber = 1;
    order.Запасы?.forEach((item, idx) => {
      const orderQty = Number(item.Количество) || 0;
      const rowKey = `${order.Ref_Key}_${idx}`;
      const acceptQtyNum = parseAcceptQty(editableAcceptQty[rowKey]);
      if (acceptQtyNum <= 0) return;
      const displayAmount = cappedAmounts[idx] ?? 0;
      if (displayAmount <= 0) return;
      const rowPrice = acceptQtyNum !== 0 ? displayAmount / acceptQtyNum : 0;
      const orderAmount = Number(item.Сумма) || 0;
      const vatRow = totalOrderAmount > 0 && orderAmount != null ? (orderAmount / totalOrderAmount) * totalVatFromOrder : 0;
      const vatLine = orderQty > 0 ? (vatRow / orderQty) * acceptQtyNum : 0;
      const hasVat = vatLine > 0;
      const row: { LineNumber: number; Номенклатура_Key: string; Характеристика_Key?: string; Количество: number; Сумма: number; Цена: number; ЕдиницаИзмерения: string; ЕдиницаИзмерения_Type: "StandardODATA.Catalog_КлассификаторЕдиницИзмерения"; СтавкаНДС_Key: string; СуммаНДС?: number; Содержание: string; Всего: number } = {
        LineNumber: lineNumber++,
        Номенклатура_Key: item.Номенклатура_Key,
        Количество: acceptQtyNum,
        Сумма: displayAmount,
        Цена: rowPrice,
        ЕдиницаИзмерения: item.ЕдиницаИзмерения ?? "",
        ЕдиницаИзмерения_Type: "StandardODATA.Catalog_КлассификаторЕдиницИзмерения",
        СтавкаНДС_Key: hasVat ? VAT_RATE_KEY_WITH_TAX : VAT_RATE_KEY,
        Содержание: item.Содержание ?? "",
        Всего: displayAmount
      };
      if (hasVat) {
        row.СуммаНДС = vatLine;
      }
      if (item.Характеристика_Key) {
        row.Характеристика_Key = item.Характеристика_Key;
      }
      rows.push(row);
    });
    return rows;
  };

  /** Выполняет отправку в 1С и сохранение Excel (вызывается после подтверждения в диалоге). */
  const executeCreateInvoice = async (order: TestOrder) => {
    if (!order.Договор_Key) {
      setCreateInvoiceMessage({ type: 'error', text: 'Нет данных о договоре' });
      return;
    }
    const запасы = buildЗапасыForOrder(order);
    
    // Вычисляем номер прогресса (количество существующих накладных + 1)
    const progressNumber = (order.Invoices?.length ?? 0) + 1;
    const deductDown = downPaymentOn ? downPaymentPct / 100 : 0;
    const deductRetention = retentionOn ? retentionPct / 100 : 0;
    const cappedAmounts = getCappedAcceptAmounts(order);
    const totalAmountToAccept = cappedAmounts.reduce((s, a) => s + a, 0);

    // База для комментария: (сумма/кол-во)*кол-во к приёмке по строкам; PPH — пропорция от суммы PPH из таблицы взаиморасчетов
    const totalOrderAmount = (order.Запасы ?? []).reduce((acc, item) => acc + (Number(item.Сумма) || 0), 0);
    const totalVatFromOrder = (order.Запасы ?? []).reduce((acc, item) => acc + (Number(item.СуммаНДС) || 0), 0);
    const totalPphOrder = (order.Contract_PphAmount != null && Number.isFinite(order.Contract_PphAmount))
      ? order.Contract_PphAmount
      : (totalOrderAmount - totalVatFromOrder) * ((order.Contract_TaxPercent ?? 0) / 100);
    let totalBase = 0;
    let totalVatProportional = 0;
    (order.Запасы ?? []).forEach((item, idx) => {
      const orderQty = Number(item.Количество) || 0;
      const rowKey = `${order.Ref_Key}_${idx}`;
      const acceptQtyNum = parseAcceptQty(editableAcceptQty[rowKey]);
      const orderAmount = Number(item.Сумма) || 0;
      const baseLine = orderQty !== 0 ? (orderAmount / orderQty) * acceptQtyNum : 0;
      totalBase += baseLine;
      const vatRow = totalOrderAmount > 0 && orderAmount != null ? (orderAmount / totalOrderAmount) * totalVatFromOrder : 0;
      totalVatProportional += orderQty > 0 ? (vatRow / orderQty) * acceptQtyNum : 0;
    });
    const totalPphProportional = totalOrderAmount > 0 ? totalPphOrder * (totalBase / totalOrderAmount) : 0;
    const vatSumForComment = totalVatProportional * Math.max(0, 1 - deductDown - deductRetention);

    const commentParts: string[] = [`Progress ${progressNumber}`];
    commentParts.push(DRIVE_LINE_PREFIX + getProgressesFolderLink());
    commentParts.push(DRIVE_PATH_PREFIX + buildDrivePath({
      organizationName: order.Organization_Name,
      contractorName: order.Counterparty_Name,
      contractName: order.Contract_Description ?? order.Contract_Number,
    }));
    if (downPaymentOn && downPaymentPct > 0) {
      commentParts.push(`- Down payment ${downPaymentPct}% - ${fmtMoney2Decimals.format(totalBase * (downPaymentPct / 100))}`);
    }
    if (retentionOn && retentionPct > 0) {
      commentParts.push(`- Retention ${retentionPct}% - ${fmtMoney2Decimals.format(totalBase * (retentionPct / 100))}`);
    }
    const amountToPayContractor = Math.max(0, totalAmountToAccept - totalBase * (deductDown + deductRetention));
    const pphForComment = totalPphProportional * Math.max(0, 1 - deductDown - deductRetention);
    if (order.Contract_TaxPercent != null && order.Contract_TaxPercent > 0) {
      commentParts.push(`Exc PPH ${order.Contract_TaxPercent}% - ${fmtMoney2Decimals.format(pphForComment)}`);
    }
    commentParts.push(`Inc VAT: 11% - ${fmtMoney2Decimals.format(vatSumForComment)}`);
    commentParts.push(`Amount to accept - ${fmtMoney2Decimals.format(amountToPayContractor)}`);
    const комментарий = commentParts.join('\n');

    setIsCreatingInvoice(true);
    setCreateInvoiceMessage(null);
    const result = await createIncomingInvoice({
      orderRef: order.Ref_Key,
      contractKey: order.Договор_Key,
      counterpartyKey: order.Контрагент_Key,
      organizationKey: order.Организация_Key,
      structuralUnitKey: order.СтруктурнаяЕдиницаРезерв_Key,
      projectKey: order.Проект_Key,
      Автор_Key: getSystemUserRefKey() ?? undefined,
      Запасы: запасы,
      Комментарий: комментарий,
      Date: acceptanceDate,
    });
    if (result.success) {
      setCreateInvoiceMessage({ type: 'success', text: 'Created' });
      getTestOrder(orderRef).then((orders) => {
        setData(orders);
        const updates: Record<string, string> = {};
        orders.forEach(o => {
          o.Запасы?.forEach((_, idx) => {
            updates[`${o.Ref_Key}_${idx}`] = '0.00';
          });
        });
        setEditableAcceptQty(prev => ({ ...prev, ...updates }));
      }).catch(() => {});
    } else {
      setCreateInvoiceMessage({ type: 'error', text: result.error || 'Ошибка создания' });
    }
    // Заполняем шаблон сертификата таблицей и предлагаем сохранить файл
    try {
      const certRows = buildCertificateRows(order, editableAcceptQty);
      const totalOrderAmount = order.Запасы?.reduce((acc, item) => acc + (item.Сумма || 0), 0) || 0;
      const certOptions = {
        organizationLegalName: order.Contract_LegalEntityName ?? undefined,
        counterpartyName: order.Counterparty_Name ?? order.Контрагент_Key ?? '',
        contractNumber: order.Contract_Number,
        contractDate: order.Contract_Date,
        certificateDate: acceptanceDate,
        settlements: {
          contractNumber: order.Contract_Number ?? '',
          orderAmount: totalOrderAmount,
          amountPaid: order.PaymentOrdersSum ?? 0,
          remaining: Math.max(0, totalOrderAmount - (order.PaymentOrdersSum ?? 0)),
          totalAmountAccept: amountToPayContractor,
          invoiceComment: комментарий,
        },
      };
      const blob = await fillCertificateTemplate(certRows, certOptions);
      await saveBlobWithPicker(blob, 'Certificate of work completion.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch (certErr: unknown) {
      const msg = certErr instanceof Error ? certErr.message : 'Ошибка формирования сертификата';
      setCreateInvoiceMessage((prev) => ({
        type: 'error',
        text: prev ? `${prev.text}. ${msg}` : msg
      }));
    }
    setIsCreatingInvoice(false);
  };

  /** По клику «Create progress file» — открыть диалог подтверждения количеств. */
  const handleCreateInvoiceClick = (order: TestOrder) => {
    setCreateInvoiceMessage(null);
    setConfirmAcceptOrder(order);
  };

  /** Подтверждение в диалоге: закрыть диалог и выполнить создание накладной и файла. */
  const handleConfirmAcceptSubmit = () => {
    if (!confirmAcceptOrder) return;
    const orderToUse = confirmAcceptOrder;
    setConfirmAcceptOrder(null);
    executeCreateInvoice(orderToUse);
  };

  /** Выгрузка акта (Certificate of work completion) для конкретного прогресса — количество, позиции и суммы из накладной. */
  const handleExportCertificate = async (order: TestOrder, invoice: PurchaseInvoice) => {
    try {
      const sortedInvoices = (order.Invoices ?? []).slice().sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
      const invIdx = sortedInvoices.findIndex((inv) => inv.Ref_Key === invoice.Ref_Key);
      const invoicesBefore: PurchaseInvoice[] = invIdx >= 0 ? sortedInvoices.slice(0, invIdx) : [];
      const certRows = buildCertificateRowsForInvoice(order, invoice, invoicesBefore);
      const totalOrderAmount = order.Запасы?.reduce((acc, item) => acc + (item.Сумма || 0), 0) || 0;
      const invoiceGrossAmount = (order.Запасы ?? []).reduce((sum, orderItem) => {
        const match = invoice.Items?.find(
          (invoiceItem) => invoiceItem.Номенклатура_Key === orderItem.Номенклатура_Key &&
            (invoiceItem.Содержание || '') === (orderItem.Содержание || '')
        );
        const orderQty = Number(orderItem.Количество) || 0;
        const invoiceQty = Number(match?.Количество) || 0;
        const orderAmount = Number(orderItem.Сумма) || 0;
        return sum + (orderQty > 0 ? (orderAmount / orderQty) * invoiceQty : 0);
      }, 0);
      const invoiceDeductions = parseInvoiceComment(invoice.Комментарий);
      const totalAmountAccept = Math.max(
        0,
        invoiceGrossAmount - (invoiceDeductions.downPaymentAmount ?? 0) - (invoiceDeductions.retentionAmount ?? 0)
      );
      const organizationLegalName = order.Contract_LegalEntityName ?? null;
      let invoiceComment = invoice.Комментарий ?? '';
      if (order.Contract_TaxPercent != null && order.Contract_TaxPercent > 0 && !invoiceComment.includes('PPH')) {
        const taxPct = order.Contract_TaxPercent / 100;
        let taxAmount = 0;
        order.Запасы?.forEach((orderItem) => {
          const match = invoice.Items?.find(invItem => invItem.Номенклатура_Key === orderItem.Номенклатура_Key && (invItem.Содержание || '') === (orderItem.Содержание || ''));
          const invQty = match ? Number(match.Количество) || 0 : 0;
          const orderQty = Number(orderItem.Количество) || 0;
          if (orderQty <= 0) return;
          const orderAmount = Number(orderItem.Сумма) || 0;
          const sumNds = Number(orderItem.СуммаНДС) || 0;
          taxAmount += ((orderAmount - sumNds) / orderQty * invQty) * taxPct;
        });
        const pphDeductionFactor = Math.max(
          0,
          1 - (invoiceDeductions.downPaymentPct ?? 0) / 100 - (invoiceDeductions.retentionPct ?? 0) / 100
        );
        taxAmount *= pphDeductionFactor;
        invoiceComment = invoiceComment ? `${invoiceComment}\nExc PPH ${order.Contract_TaxPercent}% - ${fmtMoney2Decimals.format(taxAmount)}` : `Exc PPH ${order.Contract_TaxPercent}% - ${fmtMoney2Decimals.format(taxAmount)}`;
      }
      const certOptions = {
        organizationLegalName: organizationLegalName ?? undefined,
        counterpartyName: order.Counterparty_Name ?? order.Контрагент_Key ?? '',
        contractNumber: order.Contract_Number,
        contractDate: order.Contract_Date,
        certificateDate: invoice.Date,
        settlements: {
          contractNumber: order.Contract_Number ?? '',
          orderAmount: totalOrderAmount,
          amountPaid: order.PaymentOrdersSum ?? 0,
          remaining: Math.max(0, totalOrderAmount - (order.PaymentOrdersSum ?? 0)),
          totalAmountAccept: totalAmountAccept,
          invoiceComment,
        },
      };
      const blob = await fillCertificateTemplate(certRows, certOptions);
      const fileName = `Certificate of work completion - ${invoice.Number}.xlsx`;
      await saveBlobWithPicker(blob, fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      setCreateInvoiceMessage({ type: 'success', text: `Акт Progress ${invIdx + 1} выгружен` });
      setTimeout(() => setCreateInvoiceMessage(null), 2000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ошибка выгрузки акта';
      setCreateInvoiceMessage({ type: 'error', text: msg });
    }
  };

  // Загружаем данные при изменении orderRef
  useEffect(() => {
    if (!orderRef) return;
    
    setLoading(true);
    setCreateInvoiceMessage(null);
    setAcceptanceDate(new Date().toISOString().slice(0, 10));
    getTestOrder(orderRef)
      .then((orders) => {
        setData(orders);
        const initialQty: Record<string, string> = {};
        orders.forEach(o => {
          o.Запасы?.forEach((_, idx) => {
            initialQty[`${o.Ref_Key}_${idx}`] = '0.00';
          });
        });
        setEditableAcceptQty(initialQty);
        // Инициализируем тумблеры и проценты из договора (если есть заказ)
        if (orders.length > 0) {
          const firstOrder = orders[0];
          setDownPaymentOn(true);
          setDownPaymentPct(firstOrder.Contract_PrepaymentPct ?? 0);
          setRetentionOn(true);
          setRetentionPct(firstOrder.Contract_RetentionPct ?? 0);
        }
      })
      .catch((err) => setError(err.message || 'Unknown error'))
      .finally(() => setLoading(false));
  }, [orderRef]);

  const handleRefresh = () => {
    if (!orderRef) return;
    setError('');
    setCreateInvoiceMessage(null); // Очищаем сообщение при обновлении
    setLoading(true);
    getTestOrder(orderRef)
      .then((orders) => {
        setData(orders);
        const initialQty: Record<string, string> = {};
        orders.forEach(o => {
          o.Запасы?.forEach((_, idx) => {
            initialQty[`${o.Ref_Key}_${idx}`] = '0.00';
          });
        });
        setEditableAcceptQty(initialQty);
        // Обновляем тумблеры и проценты из договора (если есть заказ)
        if (orders.length > 0) {
          const firstOrder = orders[0];
          setDownPaymentOn(true);
          setDownPaymentPct(firstOrder.Contract_PrepaymentPct ?? 0);
          setRetentionOn(true);
          setRetentionPct(firstOrder.Contract_RetentionPct ?? 0);
        }
      })
      .catch((err) => setError(err.message || 'Unknown error'))
      .finally(() => setLoading(false));
  };

  // Индикатор загрузки
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-500">
        <Loader2 className="h-8 w-8 animate-spin text-[#0078d4] mb-2" />
        <p>Loading Order...</p>
      </div>
    );
  }

  // Блок ошибки
  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-center gap-2">
        <AlertCircle className="h-5 w-5" />
        <span>Error: {error}</span>
      </div>
    );
  }

  // Если данных нет
  if (data.length === 0) {
    return (
      <div className="p-8 text-center text-gray-400 bg-white rounded border border-gray-200">
        No order found.
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      {/* Маппим данные (обычно здесь один заказ, но структура позволяет список) */}
      {data.map((order) => {
        // --- Расчет итогов для футера ---
        const totalOrderAmount = order.Запасы?.reduce((acc, item) => acc + (item.Сумма || 0), 0) || 0;
        /** Сумма по договору = итоговая сумма заказа (Order amount (include PPN)). От неё считаются DP, retention и остаток к оплате. */
        const contractAmount = totalOrderAmount;
        const dpAmount = contractAmount * ((order.Contract_PrepaymentPct ?? 0) / 100);
        const retentionAmount = contractAmount * ((order.Contract_RetentionPct ?? 0) / 100);
        /** Итого НДС по заказу; по строкам ORDER: (сумма по строке / итог по заказу) × это значение */
        const totalVatFromOrder = (order.Запасы ?? []).reduce((acc, item) => acc + (Number(item.СуммаНДС) || 0), 0);
        /** Итого PPH по заказу: из реквизита договора Contract_PphAmount или расчёт (сумма заказа − НДС) × процент PPH / 100 */
        const totalPphOrder = (order.Contract_PphAmount != null && Number.isFinite(order.Contract_PphAmount))
          ? order.Contract_PphAmount
          : (totalOrderAmount - totalVatFromOrder) * ((order.Contract_TaxPercent ?? 0) / 100);
        const hasInvoices = order.Invoices && order.Invoices.length > 0;
        // Сумма remaining для всех строк (если 0, кнопка создания накладной неактивна)
        const totalRemaining = order.Запасы?.reduce((acc, item) => {
          const orderQty = Number(item.Количество) || 0;
          const receivedQty = order.Invoices?.reduce((invAcc, inv) => {
            const m = inv.Items?.find(invItem => invItem.Номенклатура_Key === item.Номенклатура_Key && (invItem.Содержание || '') === (item.Содержание || ''));
            return invAcc + (Number(m?.Количество) || 0);
          }, 0) ?? 0;
          const remaining = Math.max(0, orderQty - receivedQty);
          return acc + remaining;
        }, 0) ?? 0;
        // Сумма Qty to accept для всех строк (из введённых значений)
        const totalQtyToAccept = order.Запасы?.reduce((acc, item, idx) => {
          const rowKey = `${order.Ref_Key}_${idx}`;
          return acc + parseAcceptQty(editableAcceptQty[rowKey]);
        }, 0) ?? 0;
        const hasRemainingToAccept = totalRemaining > 0;
        const hasQtyToAccept = totalQtyToAccept > 0;
        const cappedAcceptAmounts = getCappedAcceptAmounts(order);
        const deductDown = downPaymentOn ? downPaymentPct / 100 : 0;
        const deductRetention = retentionOn ? retentionPct / 100 : 0;
        let totalBaseAccept = 0;
        let totalVatProportional = 0;
        (order.Запасы ?? []).forEach((item, idx) => {
          const orderQty = Number(item.Количество) || 0;
          const rowKey = `${order.Ref_Key}_${idx}`;
          const acceptQtyNum = parseAcceptQty(editableAcceptQty[rowKey]);
          const orderAmount = Number(item.Сумма) || 0;
          totalBaseAccept += orderQty > 0 ? (orderAmount / orderQty) * acceptQtyNum : 0;
          const vatRow = totalOrderAmount > 0 && orderAmount != null ? (orderAmount / totalOrderAmount) * totalVatFromOrder : 0;
          totalVatProportional += orderQty > 0 ? (vatRow / orderQty) * acceptQtyNum : 0;
        });
        /** PPH в блоке Acceptance — пропорция от суммы PPH из таблицы взаиморасчетов (totalPphOrder). */
        const totalPphProportional = totalOrderAmount > 0 ? totalPphOrder * (totalBaseAccept / totalOrderAmount) : 0;
        const totalAmountToAccept = cappedAcceptAmounts.reduce((s, a) => s + a, 0);
        const dpAmountAccept = totalBaseAccept * deductDown;
        const retentionAmountAccept = totalBaseAccept * deductRetention;
        const paymentToContractorAccept = Math.max(0, totalAmountToAccept - dpAmountAccept - retentionAmountAccept);
        const vatForAccept = totalVatProportional * Math.max(0, 1 - deductDown - deductRetention);
        const pphForAccept = totalPphProportional * Math.max(0, 1 - deductDown - deductRetention);

        return (
          <div key={order.Ref_Key} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            
            {/* --- Заголовок карточки заказа: левая часть (Дата, Проект, № договора, BOQ) + таблица взаиморасчетов --- */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-start gap-6">
              {/* Левая часть: Дата, Проект, № договора, BOQ — шире для удобства */}
              <div className="space-y-4 text-sm min-w-0 md:min-w-[380px] md:max-w-[480px] pr-8">
                  <div className="flex items-center gap-4">
                      <span className="font-bold text-gray-400 w-28 flex-shrink-0">Date:</span>
                      <span className="font-medium">{new Date(order.Date).toLocaleDateString('ru-RU')}</span>
                  </div>
                  <div className="flex items-start gap-4">
                      <span className="font-bold text-gray-400 w-28 flex-shrink-0 flex items-center gap-1">Project:</span>
                      <span className="bg-blue-50 text-blue-800 px-2 py-1 rounded border border-blue-100 min-w-0" title={order.Project_Name}>
                          {order.Project_Name || order.Проект_Key}
                      </span>
                  </div>
                  <div className="flex items-center gap-4">
                      <span className="font-bold text-gray-400 w-28 flex-shrink-0 flex items-center gap-1">Contract No</span>
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200 font-mono text-sm">
                          {order.Contract_Description ?? order.Contract_Number}
                      </span>
                  </div>
                  <div className="flex items-start gap-4">
                      <span className="font-bold text-gray-400 w-28 flex-shrink-0">Counterparty:</span>
                      <span className="bg-purple-50 text-purple-800 px-2 py-1 rounded border border-purple-100 min-w-0" title={order.Counterparty_Name}>
                          {order.Counterparty_Name || order.Контрагент_Key || '—'}
                      </span>
                  </div>
                  <div className="flex flex-col gap-2">
                      <div className="font-bold text-gray-400 text-xs flex items-center gap-1 uppercase">
                        <MessageSquare className="w-3 h-3"/> BOQ:
                      </div>
                      <div className="text-gray-600 italic border-l-2 border-[#0078d4] pl-3 py-1.5 text-sm">
                          {order.Комментарий || "—"}
                      </div>
                  </div>
                  <div className="pt-2 flex flex-wrap items-center gap-2">
                      <button
                          onClick={() => handleCreateInvoiceClick(order)}
                          disabled={isCreatingInvoice || !order.Договор_Key || !hasRemainingToAccept || !hasQtyToAccept}
                          className="flex items-center gap-2 rounded bg-[#0078d4] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                          {isCreatingInvoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus className="h-4 w-4" />}
                          Create progress file
                      </button>
                      <button
                          type="button"
                          onClick={handleRefresh}
                          disabled={loading}
                          className="flex items-center justify-center rounded border border-transparent bg-transparent p-2 text-green-600 transition hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Обновить"
                      >
                          <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                      </button>
                      {createInvoiceMessage && (
                          <div className={`mt-2 text-xs px-2 py-1 rounded flex items-center gap-1.5 ${createInvoiceMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                              {createInvoiceMessage.type === 'success' && <CheckCircle className="h-3.5 w-3.5" />}
                              {createInvoiceMessage.text}
                          </div>
                      )}
                  </div>
              </div>

              {/* Таблица взаиморасчетов с поставщиком + комментарий к договору — overflow-x-auto для прокрутки на узких экранах */}
              <div className="min-w-0 overflow-x-auto flex flex-col gap-3">
                  <div className="font-semibold text-gray-700 text-sm">Current settlements with the contractor</div>
                  <div className="w-max min-w-full">
                  <table className="text-sm border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                      <thead>
                          <tr className="bg-gray-100 text-gray-600 font-semibold">
                              <th className="px-4 py-2 text-right border-b border-r border-gray-200">Order amount (include PPN), IDR</th>
                              <th className="px-4 py-2 text-right border-b border-r border-gray-200">PPN (НДС), IDR</th>
                              <th className="px-4 py-2 text-right border-b border-r border-gray-200">PPH, IDR</th>
                              <th className="px-4 py-2 text-right border-b border-r border-gray-200">Paid amount to the contractor, IDR</th>
                              <th className="px-4 py-2 text-right border-b border-gray-200">Remaining amount to be paid, IDR</th>
                          </tr>
                      </thead>
                      <tbody>
                          <tr>
                              <td className="px-4 py-2 text-right font-medium text-gray-800 border-r border-b border-gray-200">{fmtMoney2Decimals.format(contractAmount)}</td>
                              <td className="px-4 py-2 text-right font-medium text-gray-800 border-r border-b border-gray-200">{fmtMoney2Decimals.format(totalVatFromOrder)}</td>
                              <td className="px-4 py-2 text-right font-medium text-gray-800 border-r border-b border-gray-200">{fmtMoney2Decimals.format(totalPphOrder)}</td>
                              <td className="px-4 py-2 text-right font-medium text-gray-700 border-r border-b border-gray-200">{fmtMoney2Decimals.format(order.PaymentOrdersSum ?? 0)}</td>
                              <td className="px-4 py-2 text-right font-bold text-[#0078d4] border-b border-gray-200">{fmtMoney2Decimals.format(Math.max(0, contractAmount - (order.PaymentOrdersSum ?? 0)))}</td>
                          </tr>
                      </tbody>
                  </table>
                  <table className="text-sm border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                      <thead>
                          <tr className="bg-gray-100 text-gray-600 font-semibold">
                              <th className="px-4 py-2 text-right border-b border-r border-gray-200">DP amount under contract, IDR</th>
                              <th className="px-4 py-2 text-right border-b border-r border-gray-200">Warranty retention amount under contract, IDR</th>
                              <th className="px-4 py-2 text-right border-b border-gray-200">Сумма факт затрат с учетом всех налогов, IDR</th>
                          </tr>
                      </thead>
                      <tbody>
                          <tr>
                              <td className="px-4 py-2 text-right font-medium text-gray-800 border-r border-b border-gray-200">{fmtMoney2Decimals.format(dpAmount)}</td>
                              <td className="px-4 py-2 text-right font-medium text-gray-800 border-r border-b border-gray-200">{fmtMoney2Decimals.format(retentionAmount)}</td>
                              <td className="px-4 py-2 text-right font-medium text-gray-800 border-b border-gray-200">{fmtMoney2Decimals.format(totalOrderAmount + totalPphOrder)}</td>
                          </tr>
                      </tbody>
                  </table>
                  </div>
                  {order.Contract_Comment && (
                      <div className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                          <span className="font-semibold text-gray-500 uppercase">COMMENT:</span>
                          <p className="mt-1 text-gray-700 whitespace-pre-line">{order.Contract_Comment}</p>
                      </div>
                  )}
              </div>
            </div>

            {/* --- Таблица товаров (Stocks Table) --- */}
            <div className="border-t border-gray-100 p-0">
              <div className="overflow-x-auto">
                  {order.Запасы && order.Запасы.length > 0 ? (
                      <table className="table-fixed text-xs text-left border-collapse w-max">
                          <thead className="bg-gray-50 text-gray-500 uppercase font-semibold">
                              {/* Заголовки секций: Order | Progress | Acceptance */}
                              <tr>
                                  <th colSpan={9} className="px-4 py-3 border-b-2 border-r-4 border-gray-400 text-left text-base md:text-lg font-bold text-gray-800 bg-gray-100">
                                      ORDER
                                  </th>
                                  <th colSpan={hasInvoices ? (order.Invoices?.length ?? 0) * 2 + 1 : 2} className="px-4 py-3 border-b-2 border-r-4 border-gray-400 text-left text-base md:text-lg font-bold text-gray-800 bg-gray-100">
                                      PROGRESS
                                  </th>
                                  <th colSpan={2} className="px-4 py-3 border-b-2 border-l-4 border-sky-400 text-left text-base md:text-lg font-bold text-sky-800 bg-sky-100">
                                      <div className="flex flex-wrap items-center gap-3">
                                          <span>ACCEPTANCE</span>
                                          <label className="flex items-center gap-1.5 text-sm font-normal text-sky-700">
                                              <span>Date:</span>
                                              <input
                                                  type="date"
                                                  value={acceptanceDate}
                                                  onChange={(e) => setAcceptanceDate(e.target.value)}
                                                  className="px-2 py-1 text-xs border border-sky-300 rounded bg-white text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                                              />
                                          </label>
                                      </div>
                                  </th>
                              </tr>
                              {/* Второй ряд: Основные поля + Группы накладных */}
                              <tr>
                                  {/* Фиксированные (Sticky) колонки слева */}
                                  <th rowSpan={2} className={`sticky left-0 z-30 px-2 py-2 border-b border-r border-gray-200 text-center bg-gray-50 shadow-[1px_0_3px_rgba(0,0,0,0.05)] ${COL_WIDTHS.INDEX}`}>#</th>
                                  <th rowSpan={2} className={`sticky left-[50px] z-30 px-2 py-2 border-b border-r border-gray-200 bg-gray-50 shadow-[2px_0_5px_rgba(0,0,0,0.05)] ${COL_WIDTHS.RUS}`}>Product (Rus)</th>
                                  
                                  {/* Обычные колонки заказа */}
                                  <th rowSpan={2} className={`px-2 py-2 border-b border-gray-200 ${COL_WIDTHS.ENG}`}><div className="flex items-center gap-1"><Globe className="w-3 h-3"/> Eng</div></th>
                                  <th rowSpan={2} className={`px-2 py-2 border-b border-gray-200 ${COL_WIDTHS.CONTENT}`}><div className="flex items-center gap-1"><AlignLeft className="w-3 h-3"/> Content</div></th>
                                  <th rowSpan={2} className={`px-2 py-2 border-b border-gray-200 text-right ${COL_WIDTHS.QTY}`}><div className="flex items-center justify-end gap-1"><Scale className="w-3 h-3"/> Qty</div></th>
                                  <th rowSpan={2} className={`px-2 py-2 border-b border-gray-200 text-center ${COL_WIDTHS.UNIT}`}>Unit</th>
                                  <th rowSpan={2} className={`px-2 py-2 border-b border-gray-200 text-right ${COL_WIDTHS.PRICE}`}><div className="flex items-center justify-end gap-1"><Tag className="w-3 h-3"/> Price</div></th>
                                  <th rowSpan={2} className={`px-2 py-2 border-b border-r border-gray-200 text-right bg-gray-50/50 ${COL_WIDTHS.AMOUNT}`}>Amount (inc PPN)</th>
                                  <th rowSpan={2} className={`px-2 py-2 border-b border-r-4 border-gray-300 text-right bg-gray-50/50 ${COL_WIDTHS.AMOUNT}`}>PPN, IDR</th>

                                  {/* Группы накладных (ColSpan=2) ИЛИ "No Progress" — полные границы для таблицы прогресса */}
                                  {hasInvoices ? (
                                      <>
                                          {order.Invoices
                                            ?.slice()
                                            .sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())
                                            .map((invoice, invIdx) => {
                                              const driveLink = getDriveLinkFromComment(invoice.Комментарий);
                                              return (
                                              <th key={invoice.Ref_Key} colSpan={2} className="px-2 py-1 border border-green-300 bg-green-50 text-green-800 text-center">
                                                  <div className="flex flex-col items-center gap-0.5">
                                                      <span className="font-bold text-[10px] whitespace-nowrap">Progress {invIdx + 1}</span>
                                                      <span
                                                          className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold border ${
                                                              invoice.Posted
                                                                  ? 'text-green-700 bg-green-100 border-green-400'
                                                                  : 'text-red-700 bg-red-100 border-red-400'
                                                          }`}
                                                      >
                                                          {invoice.Posted ? 'Posted' : 'No posted'}
                                                      </span>
                                                      <span className="font-bold text-[11px] whitespace-nowrap">{invoice.Number}</span>
                                                      <span className="text-[9px] opacity-70 whitespace-nowrap">
                                                          {new Date(invoice.Date).toLocaleDateString('ru-RU')}
                                                      </span>
                                                      {driveLink && (
                                                          <a
                                                              href={driveLink}
                                                              target="_blank"
                                                              rel="noopener noreferrer"
                                                              className="mt-0.5 inline-flex items-center gap-0.5 rounded bg-green-600 px-1.5 py-0.5 text-[9px] font-medium text-white hover:bg-green-700"
                                                              title="Открыть папку Progresses на Drive (путь: Организация / Подрядчик / Договор)"
                                                          >
                                                              <ImagePlus className="w-3 h-3" />
                                                              Upload photos
                                                          </a>
                                                      )}
                                                      <button
                                                          type="button"
                                                          onClick={() => handleExportCertificate(order, invoice)}
                                                          className="mt-0.5 inline-flex items-center gap-0.5 rounded bg-green-600 px-1.5 py-0.5 text-[9px] font-medium text-white hover:bg-green-700"
                                                          title={`Выгрузить акт Progress ${invIdx + 1} (№ ${invoice.Number})`}
                                                      >
                                                          <FileDown className="w-3 h-3" />
                                                          Export act
                                                      </button>
                                                  </div>
                                              </th>
                                          );})}
                                      </>
                                  ) : (
                                      <th rowSpan={2} className={`px-2 py-2 border border-gray-300 bg-gray-100/50 text-gray-400 text-center italic ${COL_WIDTHS.NO_PROGRESS}`}>
                                          <div className="flex items-center justify-center gap-2">
                                            <Ban className="w-4 h-4" />
                                            <span>No progress</span>
                                          </div>
                                      </th>
                                  )}
                                  <th rowSpan={2} className={`px-2 py-2 border border-amber-300 bg-amber-50 text-amber-800 text-center text-[10px] font-semibold ${COL_WIDTHS.INV_REMAINING}`}>
                                      Remaining for acceptance
                                  </th>
                                  {/* Блок Acceptance: Qty to accept | Amount to accept */}
                                  <th rowSpan={2} className={`px-2 py-2 border-l-4 border-sky-400 border border-sky-300 bg-sky-50 text-sky-800 text-center text-[10px] font-semibold ${COL_WIDTHS.EDIT_ACCEPT_QTY}`}>
                                      <div className="flex flex-col items-center gap-1.5">
                                          <span>Qty to accept</span>
                                          <button
                                              type="button"
                                              onClick={() => {
                                                  const updates: Record<string, string> = {};
                                                  order.Запасы?.forEach((item, idx) => {
                                                      const orderQty = Number(item.Количество) || 0;
                                                      const receivedQty = order.Invoices?.reduce((acc, inv) => {
                                                          const m = inv.Items?.find(invItem => invItem.Номенклатура_Key === item.Номенклатура_Key && (invItem.Содержание || '') === (item.Содержание || ''));
                                                          return acc + (Number(m?.Количество) || 0);
                                                      }, 0) ?? 0;
                                                      const remaining = Math.max(0, orderQty - receivedQty);
                                                      updates[`${order.Ref_Key}_${idx}`] = fmtQty(remaining);
                                                  });
                                                  setEditableAcceptQty(prev => ({ ...prev, ...updates }));
                                              }}
                                              className="px-1 py-0.5 text-[9px] text-sky-700 hover:text-sky-800 hover:bg-sky-100 rounded transition border border-sky-200"
                                              title="Заполнить из Remaining for acceptance"
                                          >
                                              Fill in from remainder
                                          </button>
                                          <button
                                              type="button"
                                              onClick={() => setShowEnterPct(prev => !prev)}
                                              className="px-1 py-0.5 text-[9px] text-sky-700 hover:text-sky-800 hover:bg-sky-100 rounded transition border border-sky-200"
                                              title="Заполнить по проценту от количества из заказа"
                                          >
                                              Enter in %
                                          </button>
                                          {showEnterPct && (
                                              <div className="flex items-center gap-1 mt-0.5">
                                                  <input
                                                      type="number"
                                                      min={0}
                                                      max={100}
                                                      step={0.5}
                                                      value={enterPctValue}
                                                      onChange={(e) => setEnterPctValue(clampPct(parseFloat(e.target.value) || 100))}
                                                      className="w-12 px-0.5 py-0.5 text-xs text-right border border-sky-200 rounded text-sky-800"
                                                  />
                                                  <span className="text-[10px]">%</span>
                                                  <button
                                                      type="button"
                                                      onClick={() => {
                                                          const updates: Record<string, string> = {};
                                                          order.Запасы?.forEach((item, idx) => {
                                                              const orderQty = Number(item.Количество) || 0;
                                                              updates[`${order.Ref_Key}_${idx}`] = fmtQty(orderQty * (enterPctValue / 100));
                                                          });
                                                          setEditableAcceptQty(prev => ({ ...prev, ...updates }));
                                                          setShowEnterPct(false);
                                                      }}
                                                      className="p-0.5 text-sky-600 hover:text-sky-700 hover:bg-sky-100 rounded transition"
                                                      title="Подтвердить"
                                                  >
                                                      <Check className="w-3.5 h-3.5" />
                                                  </button>
                                              </div>
                                          )}
                                          <button
                                              type="button"
                                              onClick={() => {
                                                  const updates: Record<string, string> = {};
                                                  order.Запасы?.forEach((item, idx) => {
                                                      updates[`${order.Ref_Key}_${idx}`] = '0.00';
                                                  });
                                                  setEditableAcceptQty(prev => ({ ...prev, ...updates }));
                                              }}
                                              className="p-0.5 text-sky-600 hover:text-sky-700 hover:bg-sky-100 rounded transition"
                                              title="Обнулить все значения"
                                          >
                                              <RefreshCw className="w-3 h-3" />
                                          </button>
                                      </div>
                                  </th>
                                  <th rowSpan={2} className={`px-2 py-2 border border-sky-300 bg-sky-50 text-sky-800 text-right text-[10px] font-semibold ${COL_WIDTHS.AMOUNT_ACCEPT}`}>
                                      Amount to accept
                                  </th>
                              </tr>
                              {/* Второй ряд заголовков: под-колонки накладных (Qty | Amt) */}
                              <tr>
                                  {hasInvoices && order.Invoices
                                    ?.slice()
                                    .sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())
                                    .map((invoice, invIdx) => (
                                      <React.Fragment key={`${invoice.Ref_Key}_sub`}>
                                          <th className={`px-1 py-1 border border-green-300 bg-green-50/50 text-green-700 text-center text-[10px] ${COL_WIDTHS.INV_QTY}`}>QTY</th>
                                          <th className={`px-1 py-1 border border-green-300 bg-green-50/50 text-green-700 text-right text-[10px] ${COL_WIDTHS.INV_AMT}`}>AMOUNT</th>
                                      </React.Fragment>
                                  ))}
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                              {order.Запасы.map((item, idx) => (
                                  <tr key={idx} className="hover:bg-blue-50/20 transition-colors group">
                                      {/* Номер строки (Sticky) */}
                                      <td className={`sticky left-0 z-10 px-2 py-2 text-center font-mono text-gray-400 bg-white border-r border-gray-100 group-hover:bg-[#f8fbff] shadow-[1px_0_3px_rgba(0,0,0,0.05)] ${COL_WIDTHS.INDEX}`}>
                                          {idx + 1}
                                      </td>
                                      
                                      {/* Русское название (Sticky) */}
                                      <td className={`sticky left-[50px] z-10 px-2 py-2 font-medium text-gray-700 bg-white border-r border-gray-100 group-hover:bg-[#f8fbff] shadow-[2px_0_5px_rgba(0,0,0,0.05)] truncate ${COL_WIDTHS.RUS}`} title={item.Nomenclature_Name}>
                                          {item.Nomenclature_Name || <span className="text-red-400">Unknown</span>}
                                      </td>

                                      {/* Английское название */}
                                      <td className={`px-2 py-2 font-medium text-gray-700 truncate ${COL_WIDTHS.ENG}`} title={item.Nomenclature_Eng}>
                                          {item.Nomenclature_Eng || '-'}
                                      </td>
                                      
                                      {/* Содержание */}
                                      <td className={`px-2 py-2 text-gray-500 truncate ${COL_WIDTHS.CONTENT}`} title={item.Содержание}>
                                          {item.Содержание || '-'}
                                      </td>
                                      
                                      {/* Кол-во */}
                                      <td className={`px-2 py-2 text-right font-bold text-gray-800 ${COL_WIDTHS.QTY}`}>
                                          {fmtQty(item.Количество || 0)}
                                      </td>
                                      
                                      {/* Ед. изм */}
                                      <td className={`px-2 py-2 text-center text-gray-500 ${COL_WIDTHS.UNIT}`}>
                                          {item.Unit_Name || '-'}
                                      </td>
                                      
                                      {/* Цена */}
                                      <td className={`px-2 py-2 text-right text-gray-600 ${COL_WIDTHS.PRICE}`}>
                                          {item.Цена != null ? fmtMoney2Decimals.format(item.Цена) : '-'}
                                      </td>
                                      
                                      {/* Сумма (Жирный разделитель справа) */}
                                      <td className={`px-2 py-2 text-right font-medium text-gray-800 border-r border-gray-200 bg-gray-50/30 ${COL_WIDTHS.AMOUNT}`}>
                                          {item.Сумма != null ? fmtMoney2Decimals.format(item.Сумма) : '-'}
                                      </td>
                                      <td className={`px-2 py-2 text-right font-medium text-gray-700 border-r-4 border-gray-300 bg-gray-50/30 ${COL_WIDTHS.AMOUNT}`}>
                                        {totalOrderAmount > 0 && item.Сумма != null
                                          ? fmtMoney2Decimals.format((item.Сумма / totalOrderAmount) * totalVatFromOrder)
                                          : '-'}
                                      </td>

                                      {/* Колонки Накладных (Сопоставление): Amount = цена из заказа * Qty */}
                                      {hasInvoices ? (
                                          order.Invoices
                                            ?.slice()
                                            .sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())
                                            .map((invoice, invIdx) => {
                                              const match = invoice.Items?.find(invItem => 
                                                  invItem.Номенклатура_Key === item.Номенклатура_Key && 
                                                  (invItem.Содержание || '') === (item.Содержание || '')
                                              );
                                              const orderQtyForPrice = Number(item.Количество) || 0;
                                              const orderAmount = Number(item.Сумма) || 0;
                                              const unitPrice = orderQtyForPrice !== 0 ? orderAmount / orderQtyForPrice : 0;
                                              const invQty = match ? (Number(match.Количество) || 0) : 0;
                                              const invAmount = unitPrice * invQty;
                                              return (
                                                  <React.Fragment key={`${item.Номенклатура_Key}_${invoice.Ref_Key}`}>
                                                      <td className={`px-1 py-2 text-center border border-green-300 bg-green-50/5 text-green-700 font-bold ${COL_WIDTHS.INV_QTY}`}>
                                                          {match ? fmtQty(match.Количество) : <span className="text-gray-200 font-light">-</span>}
                                                      </td>
                                                      <td className={`px-1 py-2 text-right border border-green-300 bg-green-50/5 text-green-800 font-medium text-[11px] ${COL_WIDTHS.INV_AMT}`}>
                                                          {match ? fmtMoney2Decimals.format(invAmount) : <span className="text-gray-200 font-light">-</span>}
                                                      </td>
                                                  </React.Fragment>
                                              );
                                          })
                                      ) : (
                                          <td className={`px-2 py-2 border border-gray-300 bg-gray-50/20 ${COL_WIDTHS.NO_PROGRESS}`}></td>
                                      )}
                                      {/* Remaining for acceptance: заказано − принято */}
                                      {(() => {
                                          const orderQty = Number(item.Количество) || 0;
                                          const receivedQty = order.Invoices?.reduce((acc, inv) => {
                                              const m = inv.Items?.find(invItem => 
                                                  invItem.Номенклатура_Key === item.Номенклатура_Key && 
                                                  (invItem.Содержание || '') === (item.Содержание || '')
                                              );
                                              return acc + (Number(m?.Количество) || 0);
                                          }, 0) ?? 0;
                                          const remaining = Math.max(0, orderQty - receivedQty);
                                          const rowKey = `${order.Ref_Key}_${idx}`;
                                          const displayQty = editableAcceptQty[rowKey] !== undefined ? editableAcceptQty[rowKey] : '0.00';
                                          const displayAmount = cappedAcceptAmounts[idx] ?? 0;
                                          return (
                                              <>
                                                  <td className={`px-2 py-2 text-center border border-amber-300 bg-amber-50/30 text-amber-800 font-bold ${COL_WIDTHS.INV_REMAINING}`}>
                                                      {fmtQty(remaining)}
                                                  </td>
                                                  <td className={`px-1 py-1 border-l-4 border-sky-400 border border-sky-300 bg-sky-50/30 ${COL_WIDTHS.EDIT_ACCEPT_QTY}`}>
                                                      <input
                                                          type="text"
                                                          inputMode="decimal"
                                                          className="w-full px-1 py-0.5 text-center text-sky-800 font-medium bg-white border border-sky-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-sky-400 focus:border-sky-400"
                                                          value={displayQty}
                                                          onChange={(e) => setEditableAcceptQty(prev => ({ ...prev, [rowKey]: e.target.value }))}
                                                      />
                                                  </td>
                                                  <td className={`px-2 py-2 text-right border border-sky-300 bg-sky-50/30 text-sky-800 font-medium ${COL_WIDTHS.AMOUNT_ACCEPT}`}>
                                                      {orderQty !== 0 ? fmtMoney2Decimals.format(displayAmount) : '-'}
                                                  </td>
                                              </>
                                          );
                                      })()}
                                  </tr>
                              ))}
                          </tbody>
                          
                          {/* --- ФУТЕР (Итоговые суммы) --- */}
                          <tfoot className="bg-gray-100 font-bold text-gray-700 border-t-2 border-gray-300 text-xs">
                              <tr>
                                  <td className={`sticky left-0 z-20 px-2 py-2 bg-gray-100 border-r border-gray-300 shadow-[1px_0_3px_rgba(0,0,0,0.05)] ${COL_WIDTHS.INDEX}`}></td>
                                  <td className={`sticky left-[50px] z-20 px-2 py-2 bg-gray-100 border-r border-gray-300 shadow-[2px_0_5px_rgba(0,0,0,0.05)] ${COL_WIDTHS.RUS}`}></td>
                                  
                                  {/* Пустые ячейки под текстом */}
                                  <td className={COL_WIDTHS.ENG}></td>
                                  <td className={COL_WIDTHS.CONTENT}></td>
                                  <td className={COL_WIDTHS.QTY}></td>
                                  <td className={COL_WIDTHS.UNIT}></td>
                                  <td className={`px-2 py-2 text-right align-top bg-gray-100 border-r border-gray-300 ${COL_WIDTHS.PRICE}`}>TOTAL:</td>
                                  
                                  {/* Итого Сумма Заказа */}
                                  <td className={`px-2 py-2 text-right align-top text-gray-900 border-b border-r border-gray-300 bg-gray-200/50 ${COL_WIDTHS.AMOUNT}`}>
                                      {fmtMoney2Decimals.format(totalOrderAmount)}
                                  </td>
                                  <td className={`px-2 py-2 text-right align-top text-gray-700 border-b border-r-4 border-gray-300 bg-gray-100/50 ${COL_WIDTHS.AMOUNT}`}>
                                    {fmtMoney2Decimals.format(totalVatFromOrder)}
                                  </td>

                                  {/* Итого Суммы по Накладным: сумма (цена из заказа * Qty) по строкам */}
                                  {hasInvoices ? (
                                      order.Invoices
                                        ?.slice()
                                        .sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())
                                        .map((invoice, invIdx) => {
                                          let invTotalSum = 0;
                                          order.Запасы?.forEach(orderItem => {
                                              const match = invoice.Items?.find(invItem => 
                                                  invItem.Номенклатура_Key === orderItem.Номенклатура_Key && 
                                                  (invItem.Содержание || '') === (orderItem.Содержание || '')
                                              );
                                              if (match) {
                                                  const oq = Number(orderItem.Количество) || 0;
                                                  const oa = Number(orderItem.Сумма) || 0;
                                                  const up = oq !== 0 ? oa / oq : 0;
                                                  invTotalSum += up * (Number(match.Количество) || 0);
                                              }
                                          });
                                          const deductions = parseInvoiceComment(invoice.Комментарий);
                                          const hasDeductions = (deductions.downPaymentPct != null && deductions.downPaymentPct > 0) || (deductions.retentionPct != null && deductions.retentionPct > 0) || (deductions.pphPct != null && deductions.pphPct > 0) || (deductions.incVatAmount != null && deductions.incVatAmount > 0);
                                          const dpAmount = deductions.downPaymentAmount ?? 0;
                                          const retAmount = deductions.retentionAmount ?? 0;
                                          const paymentToContractor = invTotalSum - dpAmount - retAmount;
                                          return (
                                              <React.Fragment key={invoice.Ref_Key}>
                                                  <td className={`px-1 py-2 border border-green-300 bg-green-100/30 ${invIdx > 0 ? 'border-l border-l-green-300' : ''} ${COL_WIDTHS.INV_QTY}`}></td>
                                                  <td className={`px-1 py-2 text-right align-top border border-green-300 bg-green-100/30 ${COL_WIDTHS.INV_AMT}`}>
                                                      <div className="flex flex-col items-end gap-0.5">
                                                          <span className="text-green-800 font-bold">{fmtMoney2Decimals.format(invTotalSum)}</span>
                                                          {hasDeductions && (
                                                              <div className="text-[11px] text-green-700/90 mt-1 space-y-0.5 font-medium">
                                                                  {deductions.downPaymentPct != null && deductions.downPaymentPct > 0 && deductions.downPaymentAmount != null && (
                                                                      <div>- Down payment: {deductions.downPaymentPct}% - <span className="font-bold">{fmtMoney2Decimals.format(deductions.downPaymentAmount)}</span></div>
                                                                  )}
                                                                  {deductions.retentionPct != null && deductions.retentionPct > 0 && deductions.retentionAmount != null && (
                                                                      <div>- Retention: {deductions.retentionPct}% - <span className="font-bold">{fmtMoney2Decimals.format(deductions.retentionAmount)}</span></div>
                                                                  )}
                                                                  {deductions.pphPct != null && deductions.pphPct > 0 && deductions.pphAmount != null && (
                                                                      <div>Exc PPH: {deductions.pphPct}% - <span className="font-bold">{fmtMoney2Decimals.format(deductions.pphAmount)}</span></div>
                                                                  )}
                                                                  {deductions.incVatAmount != null && deductions.incVatAmount > 0 && (
                                                                      <div>Inc VAT: 11% - <span className="font-bold">{fmtMoney2Decimals.format(deductions.incVatAmount)}</span></div>
                                                                  )}
                                                              </div>
                                                          )}
                                                          <div className="text-[12px] text-green-800 font-font-medium mt-1 pt-1 border-t border-green-200/60">
                                                              Payment to contractor: {fmtMoney2Decimals.format(paymentToContractor)}
                                                          </div>
                                                      </div>
                                                  </td>
                                              </React.Fragment>
                                          );
                                      })
                                  ) : (
                                    <td className={`border border-gray-300 ${COL_WIDTHS.NO_PROGRESS}`}></td>
                                  )}
                                  {/* Remaining for acceptance — без суммы в футере */}
                                  <td className={`px-2 py-2 text-center border border-amber-300 bg-amber-100/50 ${COL_WIDTHS.INV_REMAINING}`}></td>
                                  <td className={`px-2 py-2 border-l-4 border-sky-400 border border-sky-300 bg-sky-100/50 ${COL_WIDTHS.EDIT_ACCEPT_QTY}`}></td>
                                  {/* Под колонкой Amount to accept: итог, DP, Retention, НДС, PPH + тумблеры */}
                                  <td className={`px-2 py-2 border border-sky-300 bg-sky-100/50 align-top ${COL_WIDTHS.AMOUNT_ACCEPT}`}>
                                      <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5 text-xs text-sky-800 font-medium">
                                          <div></div>
                                          <div className="font-bold text-right">{fmtMoney2Decimals.format(paymentToContractorAccept)}</div>
                                          {dpAmountAccept > 0 && <><div>Down payment:</div><div className="text-right">{fmtMoney2Decimals.format(dpAmountAccept)}</div></>}
                                          {retentionAmountAccept > 0 && <><div>Retention:</div><div className="text-right">{fmtMoney2Decimals.format(retentionAmountAccept)}</div></>}
                                          {vatForAccept > 0 && <><div>НДС:</div><div className="text-right">{fmtMoney2Decimals.format(vatForAccept)}</div></>}
                                          {pphForAccept > 0 && <><div>PPH:</div><div className="text-right">{fmtMoney2Decimals.format(pphForAccept)}</div></>}
                                      </div>
                                      <div className="flex flex-col gap-2 text-[10px] text-sky-800 mt-2 pt-2 border-t border-sky-200">
                                          <div className="flex items-center gap-1.5">
                                              <input
                                                  type="checkbox"
                                                  checked={downPaymentOn}
                                                  onChange={(e) => setDownPaymentOn(e.target.checked)}
                                                  className="rounded border-sky-300 text-sky-600 focus:ring-sky-400"
                                              />
                                              <span className="font-semibold">Down payment</span>
                                              <input
                                                  type="number"
                                                  min={0}
                                                  max={100}
                                                  step={0.5}
                                                  value={downPaymentPct}
                                                  onChange={(e) => setDownPaymentPct(clampPct(parseFloat(e.target.value) || 0))}
                                                  className="w-12 px-1 py-0.5 text-right border border-sky-200 rounded bg-white text-xs ml-auto"
                                              />
                                              <span>%</span>
                                          </div>
                                          <div className="flex items-center gap-1.5">
                                              <input
                                                  type="checkbox"
                                                  checked={retentionOn}
                                                  onChange={(e) => setRetentionOn(e.target.checked)}
                                                  className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-400"
                                              />
                                              <span className="font-semibold">Retention</span>
                                              <input
                                                  type="number"
                                                  min={0}
                                                  max={100}
                                                  step={0.5}
                                                  value={retentionPct}
                                                  onChange={(e) => setRetentionPct(clampPct(parseFloat(e.target.value) || 0))}
                                                  className="w-12 px-1 py-0.5 text-right border border-emerald-200 rounded bg-white text-xs ml-auto"
                                              />
                                              <span>%</span>
                                          </div>
                                      </div>
                                  </td>
                              </tr>
                          </tfoot>
                      </table>
                  ) : (
                      <div className="p-8 text-center text-gray-400 text-sm italic flex flex-col items-center gap-2">
                          <Box className="w-8 h-8 opacity-20" />
                          No stock items found in this order.
                      </div>
                  )}
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {/* Диалог подтверждения количеств Acceptance перед созданием накладной и файла */}
    {confirmAcceptOrder && (() => {
      const orderTotalAmount = confirmAcceptOrder.Запасы?.reduce((acc, item) => acc + (item.Сумма || 0), 0) ?? 0;
      /** Сумма по договору = итоговая сумма заказа (Order amount (include PPN)). От неё считаются DP, retention и остаток к оплате. */
      const contractAmount = orderTotalAmount;
      const dpAmountConfirm = contractAmount * ((confirmAcceptOrder.Contract_PrepaymentPct ?? 0) / 100);
      const retentionAmountConfirm = contractAmount * ((confirmAcceptOrder.Contract_RetentionPct ?? 0) / 100);
      const maxAcceptableAfterDPRetention = Math.max(0, contractAmount - dpAmountConfirm - retentionAmountConfirm);
      // totalProgressSum: сумма всех paymentToContractor для проведённых прогрессов (сумма после вычетов DP и retention)
      const totalProgressSum = confirmAcceptOrder.Invoices?.reduce((acc, inv) => {
        // Вычисляем invTotalSum для этого прогресса: сумма (цена из заказа * количество из накладной)
        let invTotalSum = 0;
        confirmAcceptOrder.Запасы?.forEach(orderItem => {
          const match = inv.Items?.find(invItem => 
            invItem.Номенклатура_Key === orderItem.Номенклатура_Key && 
            (invItem.Содержание || '') === (orderItem.Содержание || '')
          );
          if (match) {
            const oq = Number(orderItem.Количество) || 0;
            const oa = Number(orderItem.Сумма) || 0;
            const unitPrice = oq !== 0 ? oa / oq : 0;
            invTotalSum += unitPrice * (Number(match.Количество) || 0);
          }
        });
        const deductions = parseInvoiceComment(inv.Комментарий);
        const dpAmount = deductions.downPaymentAmount ?? 0;
        const retAmount = deductions.retentionAmount ?? 0;
        const paymentToContractor = invTotalSum - dpAmount - retAmount;
        return acc + paymentToContractor;
      }, 0) ?? 0;
      const remainingUnderContract = Math.max(0, contractAmount - (confirmAcceptOrder.PaymentOrdersSum ?? 0));
      let hasQtyError = false;
      confirmAcceptOrder.Запасы?.forEach((item, idx) => {
        const orderQty = Number(item.Количество) || 0;
        const receivedQty = confirmAcceptOrder.Invoices?.reduce((acc, inv) => {
          const m = inv.Items?.find(invItem => invItem.Номенклатура_Key === item.Номенклатура_Key && (invItem.Содержание || '') === (item.Содержание || ''));
          return acc + (Number(m?.Количество) || 0);
        }, 0) ?? 0;
        const remaining = Math.max(0, orderQty - receivedQty);
        const rowKey = `${confirmAcceptOrder.Ref_Key}_${idx}`;
        const acceptQtyNum = parseAcceptQty(editableAcceptQty[rowKey]);
        const acceptQtyRounded = Math.round(acceptQtyNum * 100) / 100;
        const remainingRounded = Math.round(remaining * 100) / 100;
        if (acceptQtyRounded > remainingRounded) hasQtyError = true;
      });
      const cappedAmountsConfirm = getCappedAcceptAmounts(confirmAcceptOrder);
      const totalAmountAccept = cappedAmountsConfirm.reduce((s, a) => s + a, 0);
      const currentDownPayment = downPaymentOn ? totalAmountAccept * (downPaymentPct / 100) : 0;
      const currentRetention = retentionOn ? totalAmountAccept * (retentionPct / 100) : 0;
      const currentPaymentToContractor = Math.max(0, totalAmountAccept - currentDownPayment - currentRetention);
      const hasAmountError = currentPaymentToContractor > remainingUnderContract + AMOUNT_EPSILON;
      // Проверка: сумма всех прогрессов включая текущий не должна превышать сумму по договору за минусом предоплаты и удержания
      // Ошибка только при реальном превышении (больше лимита более чем на погрешность плавающей точки)
      const totalWithProgress = currentPaymentToContractor + totalProgressSum;
      const diff = totalWithProgress - maxAcceptableAfterDPRetention;
      const hasDPRetentionLimitError = diff > AMOUNT_EPSILON;
      
      // Проверка: сумма текущего прогресса + оплачено не должна превышать сумму договора за вычетом гарантийного удержания
      const paidAmountWithoutTax = confirmAcceptOrder.PaymentOrdersSum ?? 0;
      const warrantyRetentionAmount = retentionAmountConfirm;
      const maxAllowedWithPayments = Math.max(0, contractAmount - warrantyRetentionAmount);
      const totalWithPayments = currentPaymentToContractor + paidAmountWithoutTax;
      const hasPaymentLimitError = totalWithPayments > maxAllowedWithPayments + AMOUNT_EPSILON;
      
      const confirmDisabled = hasQtyError || hasAmountError || hasDPRetentionLimitError || hasPaymentLimitError;

      return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setConfirmAcceptOrder(null)}>
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Confirm acceptance quantities</h3>
            <p className="text-sm text-gray-500 mt-1">Проверьте указанные количества (Qty to accept) перед отправкой в 1С и сохранением файла.</p>
          </div>
          <div className="flex-1 overflow-auto px-6 py-4">
            <table className="w-full text-sm border border-gray-200 rounded-lg">
              <thead>
                <tr className="bg-sky-50 text-sky-800 font-semibold">
                  <th className="px-3 py-2 text-left border-b border-r border-gray-200">№</th>
                  <th className="px-3 py-2 text-left border-b border-r border-gray-200">Name</th>
                  <th className="px-3 py-2 text-left border-b border-r border-gray-200">Содержание</th>
                  <th className="px-3 py-2 text-center border-b border-r border-gray-200">Unit</th>
                  <th className="px-3 py-2 text-right border-b border-gray-200">Qty to accept</th>
                </tr>
              </thead>
              <tbody>
                {confirmAcceptOrder.Запасы?.map((item, idx) => {
                  const orderQty = Number(item.Количество) || 0;
                  const receivedQty = confirmAcceptOrder.Invoices?.reduce((acc, inv) => {
                    const m = inv.Items?.find(invItem => invItem.Номенклатура_Key === item.Номенклатура_Key && (invItem.Содержание || '') === (item.Содержание || ''));
                    return acc + (Number(m?.Количество) || 0);
                  }, 0) ?? 0;
                  const remaining = Math.max(0, orderQty - receivedQty);
                  const rowKey = `${confirmAcceptOrder.Ref_Key}_${idx}`;
                  const acceptQtyNum = parseAcceptQty(editableAcceptQty[rowKey]);
                  const acceptQtyRounded = Math.round(acceptQtyNum * 100) / 100;
                  const remainingRounded = Math.round(remaining * 100) / 100;
                  const qtyExceedsOrder = acceptQtyRounded > remainingRounded;
                  return (
                    <tr
                      key={idx}
                      className={`border-b border-gray-100 ${qtyExceedsOrder ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                    >
                      <td className="px-3 py-2 text-gray-500 border-r border-gray-100">{idx + 1}</td>
                      <td className={`px-3 py-2 border-r border-gray-100 ${qtyExceedsOrder ? 'text-red-800 font-medium' : 'text-gray-800'}`}>{item.Nomenclature_Eng || item.Nomenclature_Name || '—'}</td>
                      <td className={`px-3 py-2 border-r border-gray-100 ${qtyExceedsOrder ? 'text-red-700' : 'text-gray-600'}`}>{item.Содержание || '—'}</td>
                      <td className="px-3 py-2 text-center text-gray-600 border-r border-gray-100">{item.Unit_Name || '—'}</td>
                      <td className={`px-3 py-2 text-right font-medium ${qtyExceedsOrder ? 'text-red-700' : 'text-sky-700'}`}>{fmtQty(acceptQtyNum)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col gap-3">
            {confirmDisabled && (
              <p className="text-sm text-red-600">
                {hasQtyError && "Количество к приемке + ранее принятое не должны превышать заказ. "}
                {hasAmountError && "Сумма к оплате подрядчику не должна превышать остаток по договору. "}
                {hasDPRetentionLimitError && "Сумма всех прогрессов включая текущий не должна превышать сумму по договору за минусом предоплаты и удержания. "}
                {hasPaymentLimitError && "Сумма приемки + проведенные оплаты подрядчику не должны превышать суммы договора за вычетом гарантийного удержания. "}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAcceptOrder(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAcceptSubmit}
                disabled={confirmDisabled}
                className="px-4 py-2 text-sm font-medium text-white bg-[#0078d4] rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm and create progress file
              </button>
            </div>
          </div>
        </div>
      </div>
      );
    })()}

    </>
  );
};
